import { useQuery } from "@tanstack/react-query";

import { apiGqlClient } from "~/libs/gql";
import { useTill } from "~/store";
import { graphql } from "~/types/__generated/gql";
import { GetTillProductCategoriesQuery } from "~/types/__generated/gql/graphql";
import { GetAgentsResults, useGetAgentsCache } from "../useGetAgents";
import { GetClientsResults, useGetClientsCache } from "../useGetClients";
import {
	GetCurrentCompanyResults,
	useGetCurrentCompanyCache,
} from "../useGetCurrentCompany";

const query = /* GraphQL */ `
	query GetTillProductCategories {
		getProductCategories {
			_id
			createdAt
			updatedAt
			name

			_products {
				_id
				createdAt
				updatedAt
				name
				description
				category
				type
				originalPrice
				originalPriceAddons {
					vat {
						percentage
						amount
					}
				}
				noVat
				listedPrice
				configuredOptionGroups {
					isRequired
					minimumSelect
					maximumSelect
					optionGroup

					_optionGroup {
						_id
						createdAt
						updatedAt
						name

						_options {
							_id
							createdAt
							updatedAt
							name
							originalPrice
							originalPriceAddons {
								vat {
									percentage
									amount
								}
							}
							listedPrice
							priceConfigs {
								code
								price
								rate
								isPrimaryCurrency
							}
						}
					}
				}
				priceConfigs {
					code
					price
					rate
					isPrimaryCurrency
				}

				_options {
					_id
					createdAt
					updatedAt
					name
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					priceConfigs {
						code
						price
						rate
						isPrimaryCurrency
					}
				}
			}
		}
	}
`;

export type GetTillProductCategoriesResults =
	GetTillProductCategoriesQuery["getProductCategories"];

export type TillProductCategory = GetTillProductCategoriesResults[number];
export type TillProduct =
	GetTillProductCategoriesResults[number]["_products"][number];
export type TillProductOption =
	GetTillProductCategoriesResults[number]["_products"][number]["_options"][number];

export const getTillProductCategoriesQueryKey = () => [
	"getTillProductCategories",
];

export const useGetTillProductCategories = (enabled?: boolean) => {
	const { data: company } = useGetCurrentCompanyCache();
	const { data: agents = [] } = useGetAgentsCache();
	const { data: clients = [] } = useGetClientsCache();
	const { createFileInput, updateFileInput } = useTill();

	const { data: categories = [], ...rest } = useQuery({
		enabled: enabled ?? true,
		queryKey: getTillProductCategoriesQueryKey(),
		queryFn: async () =>
			apiGqlClient
				.request(graphql(query))
				.then((res) => res.getProductCategories),
	});

	const currentAgentId = createFileInput
		? createFileInput.agent
		: updateFileInput
			? updateFileInput.agent
			: undefined;
	const currentClientId = createFileInput
		? createFileInput.client
		: updateFileInput
			? updateFileInput.client
			: undefined;

	return {
		...rest,
		data: filterProducts(
			categories,
			agents,
			clients,
			company,
			currentAgentId,
			currentClientId,
		),
	};
};

export const useGetTillProductCategoriesCache = () => {
	return useGetTillProductCategories(false);
};

export const filterProducts = (
	categories: GetTillProductCategoriesResults,
	agents: GetAgentsResults,
	clients: GetClientsResults,
	company?: GetCurrentCompanyResults,
	currentAgentId?: string | null,
	currentClientId?: string | null,
) => {
	const isShowProductConfigs = company ? company.isShowProductConfigs : false;
	const agent = currentAgentId
		? agents.find((a) => a._id === currentAgentId)
		: null;
	const client = currentClientId
		? clients.find((c) => c._id === currentClientId)
		: null;
	const filters = {
		categories: [
			...new Set(
				(agent?.hiddenProductsConfigs?.categories ?? []).concat(
					client?.hiddenProductsConfigs?.categories ?? [],
				),
			),
		],
		products: [
			...new Set(
				(agent?.hiddenProductsConfigs?.products ?? []).concat(
					client?.hiddenProductsConfigs?.products ?? [],
				),
			),
		],
		types: [
			...new Set(
				(agent?.hiddenProductsConfigs?.types ?? []).concat(
					client?.hiddenProductsConfigs?.types ?? [],
				),
			),
		],
	};
	const isFilterConfigured =
		filters.categories.length > 0 ||
		filters.products.length > 0 ||
		filters.types.length > 0;

	if (!isFilterConfigured) return categories;

	if (isShowProductConfigs) {
		categories = categories.map((c) => ({
			...c,
			_products: c._products.filter(
				(p) =>
					filters.categories.includes(p.category) ||
					filters.products.includes(p._id) ||
					filters.types.includes(p.type),
			),
		}));
	} else {
		categories = categories.map((c) => ({
			...c,
			_products: c._products.filter(
				(p) =>
					!filters.categories.includes(p.category) &&
					!filters.products.includes(p._id) &&
					!filters.types.includes(p.type),
			),
		}));
	}

	return categories;
};
